// Companion/blocks/StudySetHeader.tsx
//
// A study set draws its own header row (ORA-685).
//
// The registry's generic header is a title and nothing else, which is right for
// a block that only has content. A set also has *state* — where the student is
// in it, and how the items behind them went — and Kevin's frames put that on
// the title line rather than in a band of its own above the content. Rather
// than teach the generic header about sets, the set owns the row: the same box,
// the same type, plus a slot on the right that each kind fills for itself
// (questions do here; videos and cards in ORA-686/687).
//
// `ChatBlocks` skips its own header for any block whose registry entry says
// `ownsHeader`, so there is exactly one header either way.

import type { ReactNode } from 'react';
import type { ChatStudySetBlockPayload } from '@/types/chatBlocks';

/** The fallback heading for a set the server gave no scope label. */
const STUDY_SET_TITLES = {
  question: 'Practice Questions',
  flashcard: 'Flashcards',
  video: 'Videos',
} as const;

/**
 * What the header says a set is. The registry uses it too, so the title in the
 * thread's block list and the title drawn here can never drift apart.
 */
export const studySetTitle = (
  payload: Pick<ChatStudySetBlockPayload, 'kind' | 'scopeLabel'>
): string => payload.scopeLabel || STUDY_SET_TITLES[payload.kind];

export interface StudySetHeaderProps {
  payload: ChatStudySetBlockPayload;
  /** A restored legacy set says so, exactly as the generic header does. */
  isReadOnly: boolean;
  /** What this kind puts on the right of the row. */
  aside?: ReactNode;
}

const StudySetHeader = ({
  payload,
  isReadOnly,
  aside,
}: StudySetHeaderProps) => (
  <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 text-xxs font-medium tracking-wide text-fg-secondary uppercase">
        Study Set
      </span>
      {/* The title gives way to the aside rather than pushing it off the row. */}
      <strong className="min-w-0 truncate text-xs font-medium text-fg-primary">
        {studySetTitle(payload)}
      </strong>
    </div>
    {aside || isReadOnly ? (
      <div className="flex shrink-0 items-center gap-2">
        {aside}
        {isReadOnly ? (
          <span className="text-xxs tracking-wide text-fg-secondary uppercase">
            Read-only
          </span>
        ) : null}
      </div>
    ) : null}
  </header>
);

export default StudySetHeader;
